// Fees.tsx — orchestrator only.
//
// Sprint 5 cumulative result of the decomposition started by 5/5b/5c/5d.
// Layers, top-to-bottom:
//
//   ./fees/feeRules.ts         — pure-function business logic + types.
//   ./fees/useFeesData.ts      — store selectors, mutations, late-fee
//                                policy fetch. Zero React-render concerns.
//   ./fees/{FeeForm,Bulk,Waive,UnitFeesView}Modal.tsx
//                              — discrete CRUD modals, props in / events out.
//   ./fees/{Units,All}FeesTable.tsx, ./fees/LateFeePolicyTab.tsx
//                              — presentation (tables + the policy form tab).
//
// This file owns: page header chrome, the Tabs container, modal open/close
// state, and the thin handler glue that maps modal callbacks to hook
// actions + toast feedback. That is the entirety of the "orchestrator"
// per docs/page-component-template.md.
//
// Was ~1015 lines before Sprint 5; now ~250.

import React, { useState } from 'react';
import { Card, Button, Form, Space, Tabs, Tag } from 'antd';
import { staticApi } from '../utils/staticApi';
import {
  ApartmentOutlined,
  ExclamationCircleOutlined,
  HomeOutlined,
  PlusOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';

import { useAuth } from '../../../context/UnifiedAuthContext';
import { Fee, Owner } from '../types';

import {
  computeUnitFeeSummaries,
  getUniqueFeeTypes,
  realFeeId as toRealFeeId,
  canManageFees as roleCanManageFees,
  type UnitFeeSummary,
  type FeeTypeSummary,
} from './fees/feeRules';
import { useFeesData } from './fees/useFeesData';

import { FeeFormModal }      from './fees/FeeFormModal';
import { BulkFeeModal }      from './fees/BulkFeeModal';
import { WaiveFeeModal }     from './fees/WaiveFeeModal';
import { UnitFeesViewModal } from './fees/UnitFeesViewModal';
import { UnitsSummaryTable } from './fees/UnitsSummaryTable';
import { AllFeesTable }      from './fees/AllFeesTable';
import { LateFeePolicyTab }  from './fees/LateFeePolicyTab';
import type { LateFeePolicyFormValues } from './fees/LateFeePolicyTab';

const { TabPane } = Tabs;

export const Fees: React.FC = () => {
  // ── Data ───────────────────────────────────────────────────────────────
  const {
    fees,
    owners,
    currentCondoId,
    lateFeePolicy,
    policyLoading,
    policySaving,
    policyDeleting,
    createFee,
    deleteFeeApi,
    deleteFeeGroupApi,
    markFeeAsPaidApi,
    bulkCreateFees,
    savePolicy,
    deletePolicy,
    waiveFee: waiveFeeApi,
  } = useFeesData();
  const { user } = useAuth();
  const canManageFees = roleCanManageFees(user?.role);

  // ── UI state (modal open/close + selection) ────────────────────────────
  const [isAddModalOpen,   setIsAddModalOpen]   = useState(false);
  const [bulkModalOpen,    setBulkModalOpen]    = useState(false);
  const [bulkGenerating,   setBulkGenerating]   = useState(false);
  const [bulkForm]         = Form.useForm();
  const [isViewModalOpen,  setIsViewModalOpen]  = useState(false);
  const [selectedUnitFees, setSelectedUnitFees] = useState<UnitFeeSummary | null>(null);
  const [waiveModalOpen,   setWaiveModalOpen]   = useState(false);
  const [waivingFeeId,     setWaivingFeeId]     = useState<string | null>(null);

  // Recomputed every render — cheap, owners + fees rarely change in a
  // single render cycle, and the alternative (memoising via useMemo) makes
  // a dep-array bug class possible. See the Sprint 0 spinner-loop story.
  const unitSummaries = computeUnitFeeSummaries(owners, fees);
  const feeTypeRows   = getUniqueFeeTypes(fees, owners);

  // ── Handlers (thin glue: modal events -> hook actions + toast) ─────────

  const handleAddFee = async (values: { isCondoWide: boolean; ownerId?: string; type: string; amount: string; dueDate: string; description: string }) => {
    const isCondoWide = !values.ownerId;
    const dto: Omit<Fee, 'id'> = {
      condoId:     currentCondoId || '',
      // Sprint 135 — was `values.ownerId ?? ''`. The backend routes to the
      // condo-wide fan-out on `ownerId == null`, and `""` is NOT null; this only
      // worked because `isCondoWide: true` also satisfies that `||`. An empty
      // string would also fail the V31 owner_id → apps.users FK. Send a real
      // absence so the two signals can't disagree.
      ownerId:     values.ownerId || undefined,
      type:        values.type as Fee['type'],
      amount:      parseFloat(values.amount),
      dueDate:     new Date(values.dueDate),
      status:      'PENDING',
      description: values.description,
      isCondoWide,
    };
    try {
      await createFee(dto);
      staticApi.message.success(`${isCondoWide ? 'Condo-wide fee' : 'Individual fee'} added successfully`);
    } catch (e) {
      // Sprint 117 — surface the backend's specific RFC-7807 message (apiJson
      // sets err.message to detail / first field-error) instead of a generic
      // toast; fall back to the generic copy when none is present.
      const err = e as { message?: string };
      staticApi.message.error(err.message || 'Failed to add fee. Please try again.');
      throw e; // keep the modal open for retry
    }
  };

  const handleMarkAsPaid = async (feeId: string) => {
    try {
      await markFeeAsPaidApi(toRealFeeId(feeId));
      staticApi.message.success('Fee marked as paid');
    } catch {
      staticApi.message.error('Failed to update fee status.');
    }
  };

  // Sprint 135 (R12-2, ADR-130) — a condo-wide assessment is N per-owner rows on
  // the server shown here as ONE line, so removing that line has to remove the
  // batch. It used to pass `originalFee.id` — the representative row — which
  // deleted one owner's share and left the other N-1 charged while the line
  // vanished from this table.
  const handleRemoveFee = (row: FeeTypeSummary) => {
    const isBatch = !!row.groupId && row.totalUnits > 1;
    staticApi.modal.confirm({
      title: isBatch
        ? `Remove this assessment from all ${row.totalUnits} units?`
        : 'Are you sure you want to remove this fee?',
      icon:    <ExclamationCircleOutlined />,
      content: isBatch
        ? `Every unit's share of this charge will be removed. This action cannot be undone.`
        : 'This action cannot be undone.',
      async onOk() {
        try {
          if (isBatch) {
            await deleteFeeGroupApi(row.groupId!);
            staticApi.message.success('Assessment removed from all units');
          } else {
            await deleteFeeApi(row.originalFee.id);
            staticApi.message.success('Fee removed successfully');
          }
        } catch (err: any) {
          // Surface the server's reason — e.g. "3 of 12 owners have already paid
          // their share" — rather than a blanket failure the manager can't act on.
          staticApi.message.error(err?.message || 'Failed to remove fee.');
        }
      },
    });
  };

  const handleViewUnitFees = (row: UnitFeeSummary) => {
    setSelectedUnitFees(row);
    setIsViewModalOpen(true);
  };

  const handleWaiveFee = async (feeId: string, reason: string) => {
    try {
      await waiveFeeApi(feeId, reason);
      staticApi.message.success('Fee waived successfully');
      setWaiveModalOpen(false);
      setWaivingFeeId(null);
    } catch {
      staticApi.message.error('Failed to waive fee');
    }
  };

  const handleSavePolicy = async (values: LateFeePolicyFormValues) => {
    try {
      await savePolicy({
        gracePeriodDays: values.gracePeriodDays,
        calculationType: values.calculationType,
        flatAmount:      values.flatAmount     ?? undefined,
        percentageRate:  values.percentageRate ?? undefined,
        maxCap:          values.maxCap         ?? undefined,
        isActive:        values.isActive       ?? true,
      });
      staticApi.message.success('Late fee policy saved successfully');
    } catch {
      staticApi.message.error('Failed to save late fee policy');
    }
  };

  const handleDeletePolicy = () => {
    if (!lateFeePolicy) return;
    staticApi.modal.confirm({
      title:   'Delete Late Fee Policy?',
      content: 'This will remove automatic late fee charging for this condo.',
      okType:  'danger',
      async onOk() {
        try { await deletePolicy(); staticApi.message.success('Late fee policy deleted'); }
        catch { staticApi.message.error('Failed to delete policy'); }
      },
    });
  };

  const handleBulkGenerate = async () => {
    try {
      const values = await bulkForm.validateFields();
      setBulkGenerating(true);
      const condoOwners: Owner[] = owners.filter((o: Owner) => o.condoId === currentCondoId || true);
      const ownerIds = condoOwners.map((o: Owner) => o.id || (o as any).userId).filter(Boolean);
      if (ownerIds.length === 0) { staticApi.message.warning('No owners found for this condo'); return; }
      await bulkCreateFees({
        condoId:     currentCondoId || '',
        ownerIds,
        feeType:     values.feeType,
        amount:      values.amount,
        dueDate:     values.dueDate,
        description: values.description,
        period:      values.period || undefined,
      });
      staticApi.message.success(`Generated ${ownerIds.length} fees successfully`);
      setBulkModalOpen(false);
      // NOTE: no resetFields() here. BulkFeeModal uses `destroyOnHidden`, so
      // closing it unmounts the <Form>; calling resetFields on the now-orphaned
      // instance is what fired the "useForm is not connected to any Form element"
      // warning (Sprint 139 / R12-16). The next open re-mounts the Form fresh.
    } catch {
      // Antd Form already surfaced validation errors.
    } finally {
      setBulkGenerating(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Fees Management</h1>
          <p className="text-gray-600">
            {canManageFees
              ? 'Manage condo fees, payments, and unit summaries'
              : 'View fees and payment status for all units'}
          </p>
        </div>
        {canManageFees && (
          <Space>
            <Button
              icon={<ThunderboltOutlined />}
              // Open only — no resetFields(): the Form isn't mounted yet
              // (destroyOnHidden), so resetting here hit an unconnected instance
              // (Sprint 139 / R12-16). destroyOnHidden gives a clean form on open.
              onClick={() => setBulkModalOpen(true)}
            >
              Generate Monthly Fees
            </Button>
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => setIsAddModalOpen(true)}>
              Add New Fee
            </Button>
          </Space>
        )}
      </div>

      <Card>
        <Tabs defaultActiveKey="units">
          <TabPane tab={<span><HomeOutlined />Units Summary</span>} key="units">
            <UnitsSummaryTable rows={unitSummaries} onView={handleViewUnitFees} />
          </TabPane>

          <TabPane tab={<span><ApartmentOutlined />All Fees</span>} key="fees">
            <div className="mb-4">
              <Tag color="blue"  icon={<ApartmentOutlined />}>Condo-wide fees apply to all units</Tag>
              <Tag color="green" icon={<UserOutlined />} className="ml-2">Individual fees apply to specific units</Tag>
            </div>
            <AllFeesTable
              rows={feeTypeRows}
              canManageFees={canManageFees}
              onRemove={handleRemoveFee}
            />
          </TabPane>

          {canManageFees && (
            <TabPane tab={<span><SettingOutlined />Late Fee Policy</span>} key="late-fee-policy">
              <LateFeePolicyTab
                policy={lateFeePolicy}
                loading={policyLoading}
                saving={policySaving}
                deleting={policyDeleting}
                onSave={handleSavePolicy}
                onDelete={handleDeletePolicy}
              />
            </TabPane>
          )}
        </Tabs>
      </Card>

      <FeeFormModal
        open={isAddModalOpen}
        owners={owners}
        onCancel={() => setIsAddModalOpen(false)}
        onSubmit={async (values) => {
          await handleAddFee(values);
          setIsAddModalOpen(false);
        }}
      />

      <UnitFeesViewModal
        open={isViewModalOpen}
        summary={selectedUnitFees}
        canManageFees={canManageFees}
        onCancel={() => setIsViewModalOpen(false)}
        onMarkPaid={async (feeId) => {
          await handleMarkAsPaid(feeId);
          // Refresh the open drill-down with the new bucket math.
          if (selectedUnitFees) {
            const refreshed = computeUnitFeeSummaries(owners, fees)
              // Sprint 139 — match on rowKey; ownerId can be shared by two rows.
              .find(u => u.rowKey === selectedUnitFees.rowKey);
            if (refreshed) setSelectedUnitFees(refreshed);
          }
        }}
        onWaiveRequest={(realId) => {
          setWaivingFeeId(realId);
          setWaiveModalOpen(true);
        }}
      />

      <WaiveFeeModal
        open={waiveModalOpen}
        onCancel={() => { setWaiveModalOpen(false); setWaivingFeeId(null); }}
        onSubmit={(reason) => waivingFeeId ? handleWaiveFee(waivingFeeId, reason) : Promise.resolve()}
      />

      <BulkFeeModal
        open={bulkModalOpen}
        ownerCount={owners.length}
        isGenerating={bulkGenerating}
        formRef={bulkForm}
        onCancel={() => setBulkModalOpen(false)}
        onConfirm={handleBulkGenerate}
      />
    </div>
  );
};
